import { isInSubnet, isPrivate } from 'is-in-subnet';
import { Netmask } from 'netmask';

export const STATIC_IP_RESERVED_SUBNETS = [
  '0.0.0.0/8',
  '169.254.0.0/16',
  '127.0.0.0/8',
  '224.0.0.0/4',
];

export const PRIVATE_IP_SUBNETS = [
  '10.0.0.0/8',
  '172.16.0.0/12',
  '192.168.0.0/16',
];

export const RESERVED_TOP_LEVEL_DOMAINS = [
  '.example',
  '.invalid',
  '.local',
  '.localhost',
  '.onion',
  '.test',
];

export const IPV4_INVALID_SUBNET_MESSAGE = 'Enter a valid IPv4 subnet';
export const SINGLE_IPV4_INVALID_ERROR_MESSAGE = 'Enter a valid IPv4 address';
export const IPV4_RANGE_INVALID_ERROR_MESSAGE =
  'Remove invalid IPv4 addresses/ranges';
export const IPV4_RANGE_RESERVED_ERROR_MESSAGE =
  'Remove reserved IPv4 addresses/ranges';
export const IPV4_RANGE_PUBLIC_ERROR_MESSAGE =
  'Remove public IPv4 addresses/ranges';
export const NON_RESERVED_IPV4_ERROR_MESSAGE =
  'Enter a non-reserved IPv4 address';
export const IDENTICAL_IP_ADDRESS_GATEWAY_MESSAGE =
  'Enter a gateway that is not the same as the IP address';
export const DUPLICATE_IP_ADDRESSES = 'Remove duplicate IP addresses';
export const IPv4_REGEX =
  /^(?:(?:25[0-5]|2[0-4]?[0-9]?|1[0-9]?[0-9]?|[3-9][0-9]?|0).){3}(?:25[0-5]|2[0-4]?[0-9]?|1[0-9]?[0-9]?|[3-9][0-9]?|0)$/;

/**
 * Given a string of IP addresses, subnets and ranges
 * Transform them into a list of single ipv4 address
 * Return any error when validating the addresses, subnets and ranges
 */
const splitIPv4Addresses = (value: string) => {
  const ipAddresses: string[] = [];
  let error: string = '';
  const separators = [',', '\n'];

  const addToArrays = (value: string) => {
    if (value.includes('-')) {
      const ips = [...value.split('-').map((v) => v.trim())];
      if (!areIPv4RangesValid(ips)) {
        error = IPV4_RANGE_INVALID_ERROR_MESSAGE;
        return { ipAddresses, error };
      }
      ipAddresses.push(...ips);
    } else if (value.includes('/')) {
      const formattedSubnetIPs = ipUtils.areIPv4SubnetsValid([value.trim()]);
      if (!formattedSubnetIPs) {
        error = IPV4_INVALID_SUBNET_MESSAGE;
        return { ipAddresses, error };
      }
      ipAddresses.push(...formattedSubnetIPs);
    } else {
      if (!IPv4_REGEX.test(value.trim())) {
        error = SINGLE_IPV4_INVALID_ERROR_MESSAGE;
        return { ipAddresses, error };
      }
      ipAddresses.push(value.trim());
    }
  };

  let lastSeparatorIndex = 0;
  for (let i = 0; i < value.length; i++) {
    if (separators.includes(value[i])) {
      addToArrays(value.substring(lastSeparatorIndex, i).trim());
      lastSeparatorIndex = i + 1;
    } else if (i === value.length - 1) {
      addToArrays(value.substring(lastSeparatorIndex, i + 1).trim());
    }
  }
  if (ipAddresses.length === 0) {
    addToArrays(value);
  }
  const containDuplicates = new Set(ipAddresses).size !== ipAddresses.length;
  if (containDuplicates) {
    error = DUPLICATE_IP_ADDRESSES;
  }
  return { ipAddresses, error };
};

/***
 * Checks if a given ip or ips is within these ranges (private)
 * 10.0.0.0/8, 172.16.0.0/12, 192.168.0.0/16
 * @param value ip address
 * @returns empty string if IP is private, warning message if not
 */
const isIPPrivate = (value: string): string => {
  let message = 'Verify that the IPv4 address is private';
  if (!value) {
    return '';
  }
  let isValid = true;
  if (value.includes(',') || value.includes('\n') || value.includes('/')) {
    const { ipAddresses } = splitIPv4Addresses(value);
    message = 'Verify that all IPv4 addresses are private';
    for (const ip of ipAddresses) {
      try {
        isValid = isPrivate(ip);
        if (!isValid) {
          return message;
        }
      } catch (e) {
        return message;
      }
    }
  } else {
    try {
      isValid = isPrivate(value);
    } catch (e) {
      return message;
    }
  }

  return isValid ? '' : message;
};

/**
 * Checks if a given ip address falls within the reserved subnets
 */
const isIPv4InSubnets = (value: string, subnets: string[]): boolean => {
  if (!value) {
    return false;
  }
  let isIn = false;
  try {
    isIn = isInSubnet(value, subnets);
  } catch (e) {
    return false;
  }
  return isIn;
};

/**
 * Check if a list of ip subnets are valid like 10.0.0.0/24
 * Returns a formatted a list of ips without the subnets
 */
const areIPv4SubnetsValid = (subnetIPs: string[]): string[] | undefined => {
  const formattedIPs: string[] = [];
  for (const i in subnetIPs) {
    const index = parseInt(i);
    try {
      const block = new Netmask(subnetIPs[index]);
      // we only accept subnet mask between 8 and 32
      // subnet mask higher than 32 is invalid so it will be caught in the catch
      if (block.bitmask < 8) {
        return undefined;
      }
      formattedIPs.push(block.first);
    } catch (e) {
      return undefined;
    }
  }
  return formattedIPs;
};

/**
 * Check if a list of ip ranges are valid like 10.0.0.0-10.0.0.1, 20.0.0.0-20.0.0.1
 */
const areIPv4RangesValid = (rangeIPs: string[]): boolean => {
  for (const i in rangeIPs) {
    const index = parseInt(i);

    // There will always be an even number of IPs
    if (index % 2 === 0) {
      const firstIPOctets = rangeIPs[index].split('.');
      const secondIPOctets = rangeIPs[index + 1].split('.');

      // First 3 octets must be the same
      if (
        firstIPOctets[0] !== secondIPOctets[0] ||
        firstIPOctets[1] !== secondIPOctets[1] ||
        firstIPOctets[2] !== secondIPOctets[2]
      ) {
        return false;
      }

      // First IP's last octet must be less than second IP's last octet
      if (parseInt(firstIPOctets[3]) >= parseInt(secondIPOctets[3])) {
        return false;
      }
    }
  }
  return true;
};

const ipUtils = {
  splitIPv4Addresses,
  isIPPrivate,
  areIPv4RangesValid,
  isIPv4InSubnets,
  areIPv4SubnetsValid,
};

export default ipUtils;
